import React, { useEffect, useRef, useState, useContext } from "react";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import { Button, Spinner } from "react-bootstrap";
import moment from "moment";
import "./SensorCharts.css";
import { sensorChartColors } from "../../General/general";
import { store } from "../../Store/store";
import {
  getReturnStepSeriesToTrack,
  getStepSeriesWithSubstepInfo,
} from "../../constants/subStepMappings";

const SensorCharts = ({
  sensorData,
  verticalLineTimestamps,
  cipLineTimestampsDetailed,
  yAxisTitle,
  tooltipFormatter,
  showSpinnerOnLoad,
  isModal,
  returnStepSeriesToTrack = undefined,
}) => {
  const chartRefs = useRef([]);
  const [options, setOptions] = useState([]);

  const globalState = useContext(store);
  const selectedProductionLine = globalState.state.selectedProductionLine;

  const getReturnStepChangeTimestamps = () => {
    const group1Data = sensorData.find((data) => data.group === "1");
    if (!group1Data || !group1Data.returnSteps) return [];

    const timestamps = new Set();

    // Auto-detect which series to track if not manually specified
    const seriesToTrack =
      returnStepSeriesToTrack !== undefined
        ? returnStepSeriesToTrack
        : getReturnStepSeriesToTrack(selectedProductionLine);

    Object.keys(group1Data.returnSteps).forEach((key) => {
      const returnStep = group1Data.returnSteps[key];

      // If specific series specified, only track those
      // If seriesToTrack is null, track all
      if (seriesToTrack && !seriesToTrack.includes(returnStep.label)) {
        return; // Skip this series
      }

      const timeSeriesData = returnStep.timeSeriesData;

      if (timeSeriesData && timeSeriesData.length > 0) {
        let previousValue = timeSeriesData[0][1];

        for (let i = 1; i < timeSeriesData.length; i++) {
          const currentValue = timeSeriesData[i][1];
          if (currentValue !== previousValue) {
            timestamps.add(timeSeriesData[i][0]);
            previousValue = currentValue;
          }
        }
      }
    });

    return Array.from(timestamps).sort((a, b) => a - b);
  };

  const getXAxis = () => {
    const plotLines = [];

    // Add the thick black lines (existing functionality)
    if (cipLineTimestampsDetailed && cipLineTimestampsDetailed.length > 0) {
      plotLines.push(
        ...cipLineTimestampsDetailed.map((timestampObj) => ({
          color: "black",
          width: 3,
          value: timestampObj.timestamp,
          zIndex: 5,
        }))
      );
    } else if (verticalLineTimestamps && verticalLineTimestamps.length > 0) {
      plotLines.push(
        ...verticalLineTimestamps.map((timestamp) => ({
          color: "black",
          width: 3,
          value: timestamp,
          zIndex: 5,
        }))
      );
    }

    // Add light vertical lines for Return Step changes
    const returnStepChanges = getReturnStepChangeTimestamps();
    plotLines.push(
      ...returnStepChanges.map((timestamp) => ({
        color: "rgba(150, 150, 150, 0.3)", // Light gray with transparency
        width: 3,
        value: timestamp,
        zIndex: 3, // Lower z-index so it appears behind the thick black lines
        dashStyle: "Dash",
      }))
    );

    return plotLines.length > 0 ? { plotLines } : {};
  };

  const syncExtremes = (e) => {
    const thisChart = e.target.chart;
    if (e.trigger !== "syncExtremes") {
      Highcharts.each(Highcharts.charts, function (chart) {
        if (chart && chart.options.isModal === isModal && chart !== thisChart) {
          if (chart.xAxis[0].setExtremes) {
            chart.xAxis[0].setExtremes(e.min, e.max, undefined, false, {
              trigger: "syncExtremes",
            });
          }
        }
      });
    }
  };

  const syncTooltip = (e) => {
    const thisChart = e.target.series.chart;
    Highcharts.each(Highcharts.charts, function (chart) {
      if (chart && chart.options.isModal === isModal && chart !== thisChart) {
        const point = chart.series[0].searchPoint(
          {
            chartX: e.chartX,
            chartY: e.chartY,
          },
          true
        );

        if (point) {
          point.onMouseOver();
          chart.tooltip.refresh(point);
          chart.xAxis[0].drawCrosshair(e, point);
        }
      }
    });
  };

  useEffect(() => {
    const stepSeriesWithSubstepInfo = getStepSeriesWithSubstepInfo(
      selectedProductionLine
    );

    const sortedSensorData = sensorData.sort((a, b) => {
      return parseInt(b.group) - parseInt(a.group);
    });

    const newOptions = sortedSensorData.map((dataSet, index) => {
      const seriesData =
        dataSet.group === "1"
          ? (() => {
              let colorIndex = 0;

              const valvesSeries = Object.keys(dataSet.valves).map((key) => {
                const obj = dataSet.valves[key];
                return {
                  name: obj.label,
                  data: obj.timeSeriesData,
                  color:
                    obj.color ||
                    sensorChartColors[colorIndex++ % sensorChartColors.length],
                  visible: obj.isVisible,
                  subStepInfo: obj.subStepInfo,
                };
              });

              const returnStepsSeries = Object.keys(dataSet.returnSteps).map(
                (key) => {
                  const obj = dataSet.returnSteps[key];
                  return {
                    name: obj.label,
                    data: obj.timeSeriesData,
                    color:
                      obj.color ||
                      sensorChartColors[
                        colorIndex++ % sensorChartColors.length
                      ],
                    visible: obj.isVisible,
                    subStepInfo: obj.subStepInfo,
                  };
                }
              );

              return [...valvesSeries, ...returnStepsSeries]; // Combine both arrays.
            })()
          : dataSet.sensors.map((obj, sensorIndex) => ({
              name: obj.label,
              data: obj.timeSeriesData,
              color:
                obj.color ||
                sensorChartColors[sensorIndex % sensorChartColors.length],
              unitSymbol: obj.unitSymbol,
              visible: obj.isVisible,
            }));

      return {
        chart: {
          type: "line",
          zoomType: "x",
          events: {
            load() {
              const currentChart = this;
              currentChart.xAxis[0].update({
                events: {
                  setExtremes: syncExtremes,
                },
              });
            },
            mouseMove: syncTooltip,
          },
        },
        xAxis: {
          crosshair: true,
          events: {
            setExtremes: syncExtremes,
          },
          ...getXAxis(),
        },
        yAxis: {
          opposite: false,
          title: {
            text: "Units",
          },
        },
        series: seriesData,
        subStepMapping: dataSet.subStepMapping,
        plotLines: true,
        plotOptions: {
          series: {
            marker: {
              enabled: true,
              radius: 0,
            },
          },
        },
        tooltip: {
          shared: true,
          formatter: function () {
            if (tooltipFormatter) return tooltipFormatter(this);

            let currentStepName = "";
            if (
              cipLineTimestampsDetailed &&
              cipLineTimestampsDetailed.length > 0
            ) {
              const sortedTimestamps = [...cipLineTimestampsDetailed].sort(
                (a, b) => a.timestamp - b.timestamp
              );

              for (let i = 0; i < sortedTimestamps.length; i++) {
                if (this.x >= sortedTimestamps[i].timestamp) {
                  currentStepName = sortedTimestamps[i].stepName;
                  if (
                    i < sortedTimestamps.length - 1 &&
                    this.x >= sortedTimestamps[i + 1].timestamp
                  ) {
                    continue;
                  }
                  break;
                }
              }
            }

            const stepInfo = currentStepName
              ? `<b>Step: ${currentStepName}</b><br>`
              : "";

            return dataSet.group === "1"
              ? `<b>${moment(this.x)
                  .utc()
                  .format("llll")}</b><br>${stepInfo}${this.points
                  .map((point) => {
                    const seriesName = point.series.name;
                    const subStepNum = point.y;

                    // Only add sub-step info for these specific series
                    const shouldShowSubStep =
                      stepSeriesWithSubstepInfo.includes(seriesName);

                    if (shouldShowSubStep && subStepNum > 0) {
                      const mapping = point.series.chart.options.subStepMapping;
                      const subStepName =
                        mapping && mapping[subStepNum]
                          ? `${subStepNum} - ${mapping[subStepNum]}`
                          : subStepNum;
                      return `${seriesName} (${subStepName}): ${subStepNum}`;
                    }

                    // For all other series (valves, etc.), just show the regular format
                    return `${seriesName}: ${subStepNum}`;
                  })
                  .join("<br>")}`
              : `<b>${moment(this.x)
                  .utc()
                  .format("llll")}</b><br>${stepInfo}${this.points
                  .map(
                    (point) =>
                      `${point.series.name}: ${point.y.toFixed(2)} ${
                        point.series.options.unitSymbol
                      }`
                  )
                  .join("<br>")}`;
          },
        },
        legend: {
          enabled: true,
          layout: "vertical",
          align: "right",
          verticalAlign: "middle",
          width: 400,
          x: 60,
          y: 0,
          itemStyle: {
            fontSize: "12px",
          },
        },
        credits: {
          enabled: false,
        },
        navigator: {
          enabled: false,
        },
        rangeSelector: {
          enabled: false,
        },
        isModal: isModal,
      };
    });

    setOptions(newOptions);
  }, [sensorData]);

  return (
    <>
      {showSpinnerOnLoad && (!sensorData || sensorData.length === 0) && (
        <Spinner variant="primary" animation="border" />
      )}
      <div id="chart-container" className="chart-container">
        {sensorData.length > 0 &&
          options.map((opt, index) => (
            <div className="chart-wrapper" key={index}>
              <HighchartsReact
                highcharts={Highcharts}
                constructorType={"stockChart"}
                options={opt}
                ref={(el) => (chartRefs.current[index] = el)}
              />
            </div>
          ))}
      </div>
    </>
  );
};

export default SensorCharts;
